import React, { useContext, useEffect } from 'react'
import {
  MyselfFragment,
  MyselfQuery,
  SessionFragment,
  useMyselfQuery
} from '#src/generated/graphql'
import Myself from './Myself.gql'
import { withIdentity, WithIdentityProps as WithIdentityPropsType } from './withIdentity'
import withAuthentication from './withAuthentication'
import protect from './protect'
import { AnalyticsContext } from '../Analytics'
import { ApolloQueryResult } from 'apollo-client'
import userTraitsFromMyself from '../Analytics/userTraitsFromMyself'
import usePrevious from '#src/hooks/usePrevious'
import cookies from 'js-cookie'
import config from '#src/config'

export type WithIdentityProps = WithIdentityPropsType

function getSessionId(session: SessionFragment): string {
  if (process.env.TARGET === 'web') {
    const sessionCookie = cookies.get('deal_sn')
    if (sessionCookie) {
      return atob(sessionCookie.split('.')[1])
    }
  }

  return session.id
}

function getExperimentUserId(session: SessionFragment, myself: MyselfFragment | null): string {
  return myself ? myself.user.experimentUserId : session.experimentUserId
}

// Context
type IdentityContextType = {
  myself: MyselfFragment | null
  session: SessionFragment | null
  refetch?: () => Promise<ApolloQueryResult<MyselfQuery>>
  sessionId?: string
  userId?: string
  experimentUserId?: string
}

const IdentityContext = React.createContext<IdentityContextType>({
  myself: null,
  session: null
})

// Consumer
const IdentityConsumer = IdentityContext.Consumer

// Providers
// This provider is a small wrapper around the context provider, in order to handle the loading state
const IdentityProvider: React.SFC = ({ children }) => {
  const analytics = useContext(AnalyticsContext)
  const { data, loading, refetch: refetchMyself } = useMyselfQuery({
    partialRefetch: true
  })

  // Data exposed via context
  const myself = data?.me || null
  const session = data?.session || null
  const sessionId = data && getSessionId(data.session)
  const userId = data?.me?.user.id || undefined
  const experimentUserId = data && getExperimentUserId(data.session, data.me)
  const refetch = () => {
    return refetchMyself().then(response => {
      /**
       * We often track events after refetching the user's identity,
       *   so we need to make sure the analytics client is up to date
       *   with the latest user state (ID and traits)
       */
      const myself = response.data.me
      if (myself) {
        analytics?.identify(myself.id)
        analytics?.addTraits(userTraitsFromMyself(myself))
      }

      return response
    })
  }

  // The context object itself
  const context: IdentityContextType = {
    myself,
    session,
    sessionId,
    userId,
    experimentUserId,
    refetch
  }

  // Keep track of the previous "myself" state to determine when a user has logged-out
  const previousMyself = usePrevious(myself)

  // Listen to identity changes and update the analytics state
  useEffect(() => {
    if (!analytics) {
      return
    }

    if (myself) {
      // The authenticated user's identity has changed. They may have logged-in or are impersonating someone.
      if (myself.realUser.id !== myself.user.id) {
        // The user is being impersonated. Disable all tracking.
        analytics.disable()
      } else {
        // The user is no longer (or was never) being impersonated. Re-enable tracking.
        analytics.enable()
      }

      analytics.identify(myself.id)
      analytics.addTraits(userTraitsFromMyself(myself))

      // Remove the `deal_ust` cookie to reduce the size of our API requests, since this cookie
      //   is only relevant for registration mutations.
      cookies.remove('deal_ust', {
        domain: config.get('consumer.clientSideCookieDomain')
      })
    } else {
      // The user is unauthenticated. Persist their last-touch acquisition attributes to a cookie.
      //
      // The cookie is sent along with each request, including registration. During registration,
      //   the attributes are saved to the database for use by backend systems. After registration,
      //   we remove the cookie in order to reduce API request sizes (the backend does not update
      //   the attributes in the database once the user has been created).
      //
      // TODO: Remove the `deal_ust` cookie. The touchpoint tracking inside @deal/web-tracking is
      //   our source of truth for this data. This is now only used for the #organic-users Slack
      //   channel. This user property could instead be derived from BigQuery or passed as an
      //   explicit variable to our registration mutation(s).
      if (analytics.lastTouch) {
        cookies.set('deal_ust', JSON.stringify(analytics.lastTouch.attributes), {
          domain: config.get('consumer.clientSideCookieDomain')
        })
      }

      // The user was previously authenticated, but is not any longer (i.e. they just logged out).
      if (previousMyself && sessionId) {
        analytics.reset(sessionId)
      }
    }
  }, [myself && myself.id, myself && myself.user])

  /**
   * During SSR, the Myself query will be in a loading state initially.
   *   We don't want to redirect the user to the login page, and we can't
   *   continue rendering the application without knowing the user's identity,
   *   (unless we required every consumer of the IdentityContext to handle
   *   this loading state).
   *
   * Instead, we initially short-circuit the rendering here, and wait for the
   *   second render pass to complete (`renderToStringWithData`). After that
   *   point the identity data will always be available.
   */
  if (loading) {
    return null
  }

  return <IdentityContext.Provider value={context}>{children}</IdentityContext.Provider>
}

export {
  IdentityContext,
  IdentityContextType,
  IdentityProvider,
  IdentityConsumer,
  withIdentity,
  withAuthentication,
  protect,
  Myself
}
